import React from 'react';

import { Checkbox, MenuItem } from '@mui/material';

import { StyledAutocomplete, StyledSelect, StyledTextField } from 'components/Input/styles';
import { renderLimitedChips } from 'components/Input/limitChips';

import EntitySearchField from 'components/EntitySearchField/EntitySearchField';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField/CustomUserDropdownField';
import UserAndGroupPicker, {
  isPickerUser,
  PickerItemKind,
  PickerUser,
} from 'components/SharedFormComponents/UserAndGroupPicker';

import { NoValuePlaceholder, ValueCell } from 'containers/MemberGroups/components/RuleBuilder/styles';

import { UuidableName } from 'utils/api/types';
import { UserFieldOption } from 'utils/api/modules/user/user.types';
import { FieldConditionOperator } from 'utils/api/modules/entities/entities.types';

import { isMultiValueOperator, isNoValueOperator } from './model';
import { ValueSource } from './eventFields';

// Groups' own value control resolves from a fixed set of user attributes, so it cannot serve an
// arbitrary event field without dissolving the attribute taxonomy its tests pin. Every async branch
// below is an existing shared component; only the option select is local.

export interface ConditionValueControlProps {
  source?: ValueSource;
  operator: FieldConditionOperator;
  /** Always the wire shape: option keys, uuids or typed text. */
  values: string[];
  labels: Record<string, string>;
  onChange: (values: string[], labels?: Record<string, string>) => void;
  error?: boolean;
  testId?: string;
}

const labelFor = (labels: Record<string, string>, uuid: string): string => labels[uuid] ?? uuid;

const optionLabel = (options: UserFieldOption[], labels: Record<string, string>, uuid: string): string =>
  options.find((option) => option.uuid === uuid)?.name ?? labelFor(labels, uuid);

const namesOf = (picked: UuidableName[]): Record<string, string> =>
  Object.fromEntries(picked.map(({ uuid, name }) => [uuid, name]));

const ConditionValueControl: React.FunctionComponent<ConditionValueControlProps> = ({
  source,
  operator,
  values,
  labels,
  onChange,
  error,
  testId = 'rule-condition-value',
}) => {
  if (isNoValueOperator(operator) || source?.kind === 'none') {
    return <NoValuePlaceholder aria-hidden />;
  }

  const multiple = isMultiValueOperator(operator);

  if (source?.kind === 'search') {
    if (!source.lookupType) {
      return (
        <ValueCell data-testid={testId}>
          <StyledTextField fullWidth size="small" disabled placeholder="Unavailable" />
        </ValueCell>
      );
    }
    const selected = values.map((uuid) => ({ uuid, name: labelFor(labels, uuid) }));
    return (
      <ValueCell data-testid={testId}>
        <EntitySearchField
          field="condition-value"
          module={source.lookupType}
          multiple={multiple}
          maxChips={1}
          error={error}
          data={multiple ? selected : (selected[0] ?? null)}
          updateField={(_, value) => {
            const picked = value === null ? [] : Array.isArray(value) ? value : [value];
            onChange(
              picked.map(({ uuid }) => uuid),
              namesOf(picked)
            );
          }}
        />
      </ValueCell>
    );
  }

  if (source?.kind === 'user') {
    if (multiple) {
      const picked: PickerUser[] = values.map((uuid) => ({
        kind: PickerItemKind.User,
        uuid,
        name: labelFor(labels, uuid),
      }));
      return (
        <ValueCell data-testid={testId}>
          <UserAndGroupPicker
            value={picked}
            error={error}
            placeholder="Select users"
            maxChips={1}
            onChange={(next) => {
              const users = next.filter(isPickerUser).map(({ uuid, name }) => ({ uuid, name }));
              onChange(
                users.map(({ uuid }) => uuid),
                namesOf(users)
              );
            }}
          />
        </ValueCell>
      );
    }
    return (
      <ValueCell data-testid={testId}>
        <CustomUserDropdownField
          value={values[0] ? { uuid: values[0], name: labelFor(labels, values[0]) } : null}
          error={error}
          description=""
          onChange={(user) => (user ? onChange([user.uuid], namesOf([user])) : onChange([]))}
        />
      </ValueCell>
    );
  }

  if (source?.kind === 'options') {
    const { options } = source;
    if (multiple) {
      const selected = values.map((uuid) => ({ uuid, name: optionLabel(options, labels, uuid) }));
      const known = new Set(options.map((option) => option.uuid));
      const choices: UuidableName[] = [
        ...options.map(({ uuid, name }) => ({ uuid, name })),
        // A deactivated option stays selectable, so opening a rule cannot silently drop it.
        ...selected.filter((option) => !known.has(option.uuid)),
      ];
      return (
        <ValueCell data-testid={testId}>
          <StyledAutocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={choices}
            renderValue={renderLimitedChips((option: UuidableName) => option.name, 1)}
            value={selected}
            getOptionLabel={(option: UuidableName) => option.name}
            isOptionEqualToValue={(a: UuidableName, b: UuidableName) => a.uuid === b.uuid}
            renderOption={(props, option: UuidableName, { selected: isSelected }) => (
              <li {...props} key={option.uuid}>
                <Checkbox checked={isSelected} />
                {option.name}
              </li>
            )}
            onChange={(_, chosen) => {
              const picked = chosen as UuidableName[];
              onChange(
                picked.map(({ uuid }) => uuid),
                namesOf(picked)
              );
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                size="small"
                error={error}
                placeholder={selected.length ? '' : 'Select values'}
                inputProps={{ ...params.inputProps, 'aria-label': 'Value' }}
              />
            )}
          />
        </ValueCell>
      );
    }
    const selectedUuid = values[0] ?? '';
    const staleSelected = selectedUuid && !options.some((option) => option.uuid === selectedUuid);
    return (
      <ValueCell data-testid={testId}>
        <StyledSelect
          fullWidth
          size="small"
          displayEmpty
          error={error}
          value={selectedUuid}
          inputProps={{ 'aria-label': 'Value' }}
          onChange={(event) => {
            const uuid = event.target.value as string;
            onChange(uuid ? [uuid] : [], uuid ? { [uuid]: optionLabel(options, labels, uuid) } : {});
          }}
        >
          <MenuItem value="" disabled>
            Select a value
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.uuid} value={option.uuid}>
              {option.name}
            </MenuItem>
          ))}
          {staleSelected && <MenuItem value={selectedUuid}>{labelFor(labels, selectedUuid)}</MenuItem>}
        </StyledSelect>
      </ValueCell>
    );
  }

  return (
    <ValueCell data-testid={testId}>
      <StyledTextField
        fullWidth
        size="small"
        value={values[0] ?? ''}
        placeholder="Enter a value"
        error={error}
        inputProps={{ 'aria-label': 'Value' }}
        onChange={(event) => onChange(event.target.value ? [event.target.value] : [])}
      />
    </ValueCell>
  );
};

export default ConditionValueControl;
